// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// based on https://www.w3.org/TR/wai-aria-practices/examples/accordion/accordion.html
import AccordionContext, { AccordionContextData } from "@insite/mobius/Accordion/AccordionContext";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { applyPropBuilder } from "@insite/mobius/utilities";
import get from "@insite/mobius/utilities/get";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import styled, { ThemeProps, withTheme } from "styled-components";

export interface AccordionPresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<AccordionComponentProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type AccordionComponentProps = MobiusStyledComponentProps<"dl", AccordionContextData>;

export type AccordionProps = AccordionPresentationProps & AccordionComponentProps;

// xng-change - EIS-4195 - replace dl with div because we used divs instead of dt,dd to match our html structure
const AccordionStyle = styled.div.attrs({
    role: "presentation",
})`
    margin: 0;
    ${injectCss}
`;

const Accordion: React.FC<AccordionProps & ThemeProps<BaseTheme>> = ({
    children,
    headingLevel,
    mergeCss,
    ...otherProps
}) => {
    const { applyStyledProp } = applyPropBuilder(otherProps, { component: "accordion" });

    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.accordion?.defaultProps?.mergeCss;

    return (
        <AccordionContext.Provider value={{ headingLevel }}>
            <AccordionStyle {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
                {children}
            </AccordionStyle>
        </AccordionContext.Provider>
    );
};

/** @component */
export default withTheme(Accordion);

export { AccordionStyle };
